import { useEffect, useState } from 'react';
import { planToText, deliveryText } from '../utils/shoppingPlan';
import { printPlan } from '../utils/printPlan';

// "תוכנית הקנייה המשתלמת" ברשימת הקניות (BasketTab): ההמלצה איפה לקנות כל מוצר, סכום לכל חנות,
// והשוואה בין חנות אחת / שתיים / שלוש. החישוב ב-utils/shoppingPlan.js (levels, recommended).
// threshold - "לפצל רק אם חוסכים לפחות ₪X לכל חנות נוספת" (נשמר בדפדפן, BasketTab).
// items - כל הרשימה (לשיתוף / הדפסה, כולל מוצרים שלא נמכרים כרגע). השיתוף וההדפסה הם של התוכנית שמוצגת.

export const THRESHOLD_OPTIONS = [0, 5, 10, 20, 30, 50];

const formatPrice = (n) => `₪${Number(n || 0).toFixed(2)}`;
const formatQty = (x) => (x.isWeighable ? `${x.qty} ק"ג` : `×${x.qty}`);
const storesLabel = (count) => (count === 1 ? 'חנות אחת' : `${count} חנויות`);
const joinNames = (names) => (names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} ו${names[names.length - 1]}`);
const planNames = (plan) => joinNames(plan.groups.map((g) => g.store.name));

const s = {
  card:      { background: '#eef4ff', border: '1px solid #b6ccf5', borderRadius: 14, padding: 18 },
  title:     { margin: 0, fontSize: 18, fontWeight: 700 },
  headline:  { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' },
  recText:   { margin: 0, fontSize: 20, fontWeight: 700, color: '#1a3f9c' },
  recTotal:  { fontSize: 28, fontWeight: 700 },
  savings:   { margin: '4px 0 0', fontSize: 15, fontWeight: 700, color: '#1a7f37' },
  info:      { margin: '6px 0 0', fontSize: 14, color: '#1f2328' },
  muted:     { margin: '6px 0 0', fontSize: 13, color: '#59636e' },
  titleRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 },
  modes:     { display: 'inline-flex', background: '#fff', border: '1px solid #b6ccf5', borderRadius: 10, padding: 3 },
  modeBtn:   { padding: '6px 12px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 8, background: 'none', color: '#59636e' },
  modeOn:    { background: '#1a3f9c', color: '#fff' },
  deliveryRow: { display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 13, color: '#1f2328', padding: '2px 0 6px', marginBottom: 4, borderBottom: '1px dashed #e5e7eb' },
  toFree:    { color: '#1a7f37', fontWeight: 600 },
  shareRow:  { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12 },
  waBtn:     { padding: '7px 14px', fontSize: 14, fontWeight: 600, borderRadius: 10, background: '#1a7f37', color: '#fff', textDecoration: 'none' },
  printBtn:  { padding: '7px 14px', fontSize: 14, fontWeight: 600, border: '1px solid #1a3f9c', borderRadius: 10, background: '#fff', color: '#1a3f9c' },
  groups:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, marginTop: 14 },
  group:     { background: '#fff', border: '1px solid #d0d7de', borderRadius: 12, padding: 12 },
  groupHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, paddingBottom: 6, marginBottom: 6, borderBottom: '1px solid #eee' },
  groupName: { fontWeight: 700, fontSize: 16 },
  groupSum:  { fontWeight: 700, fontSize: 16 },
  itemRow:   { display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0', fontSize: 14 },
  itemName:  { flex: 1, minWidth: 0 },
  limitNote: { display: 'block', fontSize: 12, color: '#9a6700' },
  itemQty:   { color: '#59636e', marginInlineStart: 4, whiteSpace: 'nowrap' },
  compare:   { marginTop: 16, background: '#fff', border: '1px solid #d0d7de', borderRadius: 12, padding: '10px 12px' },
  compHead:  { margin: '0 0 6px', fontSize: 14, fontWeight: 700 },
  level:     { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '7px 0', borderTop: '1px solid #f0f0f0', fontSize: 14 },
  levelMain: { flex: 1, minWidth: 180 },
  levelSum:  { fontWeight: 700 },
  tagRec:    { fontSize: 12, fontWeight: 700, color: '#fff', background: '#1a7f37', borderRadius: 6, padding: '1px 7px' },
  tagShown:  { fontSize: 12, fontWeight: 600, color: '#1a3f9c' },
  showBtn:   { padding: '4px 10px', fontSize: 13, fontWeight: 600, border: '1px solid #1a3f9c', borderRadius: 8, background: '#fff', color: '#1a3f9c' },
  diffGood:  { fontSize: 13, color: '#1a7f37', fontWeight: 600 },
  diffSmall: { fontSize: 13, color: '#59636e' },
  threshold: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12, fontSize: 14 },
  select:    { padding: '5px 8px', border: '1px solid #d0d7de', borderRadius: 8, background: '#fff', fontSize: 14 },
  backBtn:   { background: 'none', border: 'none', padding: 0, color: '#1a3f9c', fontWeight: 600, fontSize: 14, textDecoration: 'underline' },
};

// mode - 'store' (קנייה בחנות) | 'delivery' (משלוח: דמי משלוח ומינימום הזמנה של כל חנות). stores - לצורך הסבר כשאין משלוח אפשרי
const ShoppingPlan = ({ levels, recommended, threshold, onThresholdChange, items, mode, onModeChange, stores }) => {
  const [shownKey, setShownKey] = useState(null);   // תוכנית חלופית שהמשתמש בחר להציג; null = ההמלצה
  const [printBlocked, setPrintBlocked] = useState(false);

  // הרשימה השתנתה והתוכנית שנבחרה כבר לא קיימת - חוזרים להמלצה
  useEffect(() => {
    if (shownKey && !levels.some((p) => p.key === shownKey)) setShownKey(null);
  }, [levels, shownKey]);

  const modeSwitch = (
    <div style={s.modes} role="group" aria-label="איך קונים">
      {[['store', '🏪 קנייה בחנות'], ['delivery', '🚚 משלוח']].map(([key, label]) => (
        <button key={key} type="button" aria-pressed={mode === key} style={{ ...s.modeBtn, ...(mode === key ? s.modeOn : null) }}
                onClick={() => onModeChange(key)}>{label}</button>
      ))}
    </div>
  );

  // משלוח: אף צירוף לא עומד במינימום ההזמנה של החנויות
  if (!recommended) {
    if (mode !== 'delivery') return null;
    const mins = stores.filter((st) => st.deliveryMinOrder != null).map((st) => `${st.name} - ${formatPrice(st.deliveryMinOrder)}`);
    return (
      <section style={s.card} aria-labelledby="plan-title">
        <div style={s.titleRow}><h2 id="plan-title" style={s.title}>📋 תוכנית הקנייה המשתלמת</h2>{modeSwitch}</div>
        <p style={s.recText} role="status">אי אפשר להזמין את הרשימה הזו במשלוח</p>
        <p style={s.info}>
          הסכום לא מגיע למינימום ההזמנה למשלוח{mins.length ? ` (${mins.join(', ')})` : ''}, או שמוצר נמכר רק בחנות שבה הרשימה מתחת למינימום.
          אפשר להוסיף מוצרים, או לעבור ל"קנייה בחנות".
        </p>
      </section>
    );
  }

  const shown = levels.find((p) => p.key === shownKey) || recommended;
  const isRec = shown.key === recommended.key;
  const first = levels[0];
  const noSingleStore = first.storeCount > 1;
  // תוכנית זולה יותר שלא הומלצה (חוסכת פחות מהסף)
  const cheaperNotRecommended = levels.find((p) => p.storeCount > recommended.storeCount && p.total < recommended.total - 0.005);

  return (
    <section style={s.card} aria-labelledby="plan-title">
      <div style={s.titleRow}><h2 id="plan-title" style={s.title}>📋 תוכנית הקנייה המשתלמת</h2>{modeSwitch}</div>

      <div style={s.headline} role="status">
        <p style={s.recText}>
          {!isRec ? `תוכנית חלופית: ${storesLabel(shown.storeCount)} - ${planNames(shown)}`
            : shown.storeCount === 1 ? `הכי משתלם: כל הרשימה ב${shown.groups[0].store.name}`
            : `הכי משתלם: לפצל בין ${planNames(shown)}`}
        </p>
        <span style={s.recTotal} className="num">{formatPrice(shown.total)}</span>
      </div>

      {isRec && recommended.storeCount > 1 && recommended.key !== first.key && (
        <p style={s.savings}>
          חוסך {formatPrice(first.total - recommended.total)} לעומת{' '}
          {first.storeCount === 1 ? `הכל ב${first.groups[0].store.name}` : `${storesLabel(first.storeCount)} (${planNames(first)})`}
        </p>
      )}
      {isRec && noSingleStore && (
        <p style={s.info}>אין חנות אחת שיש בה את כל המוצרים ברשימה, ולכן צריך לפחות {storesLabel(first.storeCount)}.</p>
      )}
      {isRec && cheaperNotRecommended && (
        <p style={s.muted}>
          פיצול ל{storesLabel(cheaperNotRecommended.storeCount)} חוסך רק {formatPrice(recommended.total - cheaperNotRecommended.total)} -
          פחות מ-{formatPrice(threshold)} לכל חנות נוספת, ולכן לא כדאי. אפשר לשנות את הסף למטה.
        </p>
      )}
      {!isRec && (
        <p style={s.info}>
          {shown.total < recommended.total
            ? `זול ב-${formatPrice(recommended.total - shown.total)} מההמלצה, אבל דורש עוד ${storesLabel(shown.storeCount - recommended.storeCount)}.`
            : `יקר ב-${formatPrice(shown.total - recommended.total)} מההמלצה.`}{' '}
          <button type="button" style={s.backBtn} onClick={() => setShownKey(null)}>חזרה להמלצה</button>
        </p>
      )}

      {/* שיתוף והדפסה של התוכנית המוצגת */}
      <div style={s.shareRow}>
        <a style={s.waBtn} href={`https://wa.me/?text=${encodeURIComponent(planToText(shown, items))}`}
           target="_blank" rel="noopener noreferrer">📤 שליחה בוואטסאפ</a>
        <button type="button" style={s.printBtn} onClick={() => setPrintBlocked(!printPlan(shown, items))}>
          🖨️ הדפסה / PDF
        </button>
        {printBlocked && (
          <span style={s.muted} role="alert">הדפדפן חסם חלון קופץ - צריך לאשר חלונות קופצים לאתר ולנסות שוב</span>
        )}
      </div>

      {/* מה קונים בכל חנות */}
      <div style={s.groups}>
        {shown.groups.map((g) => (
          <div key={g.store.id} style={s.group}>
            <div style={s.groupHead}>
              <span style={s.groupName}>{g.store.name}</span>
              <span style={s.groupSum} className="num">{formatPrice(g.total)}</span>
            </div>
            {g.delivery && (
              <div style={s.deliveryRow}>
                <span>🚚 {deliveryText(g.delivery)}</span>
                {g.delivery.toFree > 0 && <span style={s.toFree}>עוד {formatPrice(g.delivery.toFree)} למשלוח חינם</span>}
              </div>
            )}
            {g.items.map(({ item, price, cost, limited, limit }) => (
              <div key={item.id} style={s.itemRow}>
                <span style={s.itemName}>
                  {item.name}
                  {(item.isWeighable || item.qty !== 1) && <span style={s.itemQty}>{formatQty(item)}</span>}
                  {limited && <span style={s.limitNote}>מבצע עד {limit} יח', השאר במחיר רגיל</span>}
                </span>
                <span className="num" title={item.qty !== 1 ? `${formatPrice(price)} ליחידה` : undefined}>{formatPrice(cost)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* השוואה בין מספרי חנויות */}
      {levels.length > 1 && (
        <div style={s.compare}>
          <h3 style={s.compHead}>כל האפשרויות</h3>
          {levels.map((p, i) => {
            const prev = levels[i - 1];
            const diff = prev ? prev.total - p.total : 0;
            const isShown = p.key === shown.key;
            return (
              <div key={p.key} style={s.level}>
                <span style={s.levelMain}>
                  <b>{storesLabel(p.storeCount)}:</b> {planNames(p)}
                  {prev && (
                    <> · <span style={diff >= threshold * (p.storeCount - prev.storeCount) ? s.diffGood : s.diffSmall}>
                      חוסך עוד {formatPrice(diff)}
                    </span></>
                  )}
                </span>
                <span style={s.levelSum} className="num">{formatPrice(p.total)}</span>
                {p.key === recommended.key && <span style={s.tagRec}>מומלץ</span>}
                {isShown
                  ? <span style={s.tagShown}>מוצג</span>
                  : <button type="button" style={s.showBtn} onClick={() => setShownKey(p.key === recommended.key ? null : p.key)}>הצגה</button>}
              </div>
            );
          })}
        </div>
      )}

      {levels.length > 1 && (
        <label style={s.threshold}>
          לפצל רק אם חוסכים לפחות
          <select style={s.select} value={threshold} onChange={(e) => onThresholdChange(Number(e.target.value))}>
            {THRESHOLD_OPTIONS.map((v) => <option key={v} value={v}>{v === 0 ? 'כל סכום' : `₪${v}`}</option>)}
          </select>
          לכל חנות נוספת
        </label>
      )}
    </section>
  );
};

export default ShoppingPlan;
